import { Fragment, type ReactNode } from "react"
import type { StoreRichTextDocument } from "../../types"
import styles from "./product-page-sections.module.css"

type RichTextMark = {
  type?: string
  attrs?: Record<string, unknown>
}

type RichTextNode = {
  type?: string
  text?: string
  attrs?: Record<string, unknown>
  marks?: RichTextMark[]
  content?: RichTextNode[]
}

type RichTextProps = {
  document?: StoreRichTextDocument | null
  className?: string
  headingIdPrefix?: string
}

const isSafeHref = (href: string) => {
  if (href.startsWith("/") && !href.startsWith("//")) return true

  try {
    return new URL(href).protocol === "https:"
  } catch {
    return false
  }
}

const renderMarkedText = (node: RichTextNode, key: string): ReactNode => {
  let content: ReactNode = node.text ?? ""

  for (const [index, mark] of (node.marks ?? []).entries()) {
    const markKey = `${key}-mark-${index}`

    if (mark.type === "bold") {
      content = <strong key={markKey}>{content}</strong>
    } else if (mark.type === "italic") {
      content = <em key={markKey}>{content}</em>
    } else if (mark.type === "strike") {
      content = <s key={markKey}>{content}</s>
    } else if (
      mark.type === "link" &&
      typeof mark.attrs?.href === "string" &&
      isSafeHref(mark.attrs.href)
    ) {
      const target = mark.attrs.target === "_blank" ? "_blank" : "_self"
      content = (
        <a
          key={markKey}
          href={mark.attrs.href}
          target={target}
          rel={target === "_blank" ? "noreferrer" : undefined}
        >
          {content}
        </a>
      )
    }
  }

  return <Fragment key={key}>{content}</Fragment>
}

const renderNodes = (
  nodes: RichTextNode[] | undefined,
  prefix: string,
  headingIdPrefix?: string
): ReactNode =>
  nodes?.map((node, index) => {
    const key = `${prefix}-${index}`

    if (node.type === "text") return renderMarkedText(node, key)
    if (node.type === "hardBreak") return <br key={key} />

    const children = renderNodes(node.content, key, undefined)

    switch (node.type) {
      case "paragraph":
        return <p key={key}>{children}</p>
      case "heading": {
        const level = Number(node.attrs?.level)
        const Heading = level === 2 ? "h2" : level === 3 ? "h3" : "h4"
        return (
          <Heading
            key={key}
            id={headingIdPrefix ? `${headingIdPrefix}-${index}` : undefined}
          >
            {children}
          </Heading>
        )
      }
      case "bulletList":
        return <ul key={key}>{children}</ul>
      case "orderedList":
        return <ol key={key}>{children}</ol>
      case "listItem":
        return <li key={key}>{children}</li>
      case "blockquote":
        return <blockquote key={key}>{children}</blockquote>
      default:
        return null
    }
  }) ?? null

export default function RichText({ document, className, headingIdPrefix }: RichTextProps) {
  if (!document?.content) return null

  return (
    <div className={[styles.richText, className].filter(Boolean).join(" ")}>
      {renderNodes(document.content as RichTextNode[], "rich", headingIdPrefix)}
    </div>
  )
}
